import { FC, createRef, forwardRef, useMemo } from "react";
import "../../../assets/css/colors.css";
import "../../../assets/css/size.css";
import "../../../assets/css/string.css";
import { GridHeaderColumnsType, TableComponentProps } from "../../interface";
import SlickGrid1, { withDynamicColumns } from "../SlickGrid1";
import type { GridRowBase, QueryShape, SlickgridReactInstanceX, ValidatedColumnDefinition } from "../SlickGrid1";

type GridInstance = SlickgridReactInstanceX<GridRowBase>;

/**
 * `TableComponentProps` declares `headerColumns` required, but the one caller
 * (`dashboardComponents/widgetTable`) reaches this wrapper through a `Partial<...>` alias and can
 * leave it out, so the guard in `columnDefinitions` is live. Widened here rather than in the shared
 * Syncfusion interface, so the exported contract below stays `FC<TableComponentProps>`.
 */
type TableComponentRuntimeProps = Omit<TableComponentProps, "headerColumns"> & {
  headerColumns?: GridHeaderColumnsType[];
};

/**
 * `dataSource` and `query` reach this wrapper as `unknown`, and both grid types are open records
 * whose every member is optional (`QueryShape` even carries an index signature) — so "is a plain
 * object" is the entire contract, exactly as SlickGrid1's own `isQueryShape` puts it (which the
 * grid itself falls back to `null` on, SlickGrid1/index.tsx:2140). Checked here instead of
 * asserted, so a non-object never reaches the grid. The non-object arm is unreachable today: the
 * sole consumer (`dashboardComponents/widgetTable`) always passes a plain object `query` bag.
 */
const isQueryShape = (value: unknown): value is QueryShape =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const isGridRow = (value: unknown): value is GridRowBase => typeof value === "object" && value !== null;

const TableComponent: FC<TableComponentProps> = forwardRef<GridInstance, TableComponentRuntimeProps>(
  ({ headerColumns, dataSource, query, remoteDataSource, ...rest }, ref) => {
    /*******************
@Purpose : table Component
@Parameter : TableComponentProps
@Author : Techture
 *******************/

    const gridRef = ref || createRef<GridInstance>();

    // Convert Syncfusion-style headerColumns to SlickGrid1 columnDefinitions. Every column here is
    // derived from the `headerColumns` prop at runtime — its `field` is an arbitrary string that
    // cannot satisfy `keyof GridRowBase` — so the list goes through SlickGrid1's public constructor
    // for runtime-derived columns (`withDynamicColumns`), which is where the brand is minted.
    const columnDefinitions = useMemo<ValidatedColumnDefinition<GridRowBase>[]>(() => {
      if (!headerColumns) return [];
      return withDynamicColumns<GridRowBase>(
        headerColumns.map((col: GridHeaderColumnsType, index: number) => ({
          id: col.field || `col_${index}`,
          field: col.field || "",
          name: col.headerText || "",
          clipMode: col.clipMode || "EllipsisWithTooltip",
          formatter: col.template,
          disableFiltering: col.allowFiltering === false,
          disableSorting: col.allowSorting === false,
        })),
      );
    }, [headerColumns]);

    // `dataSource` is `unknown[]`, so the rows are checked rather than asserted here: a non-object
    // entry is DROPPED instead of reaching the grid. That is a deliberate behaviour change from the
    // old blanket assertion, and it is unreachable today — the sole consumer of this wrapper
    // (`dashboardComponents/widgetTable`) is remote-sourced and never passes `dataSource` — while a
    // non-object entry could never have rendered anyway: `ensureIds` (SlickGrid1/slickGridUtils.ts)
    // reads `data.id` off every item, which throws on `null`/`undefined` and spreads a primitive
    // into a garbage row. Memoised so the grid still sees one array identity per `dataSource`.
    const dataset = useMemo(() => dataSource?.filter(isGridRow), [dataSource]);

    return (
      // This wrapper is row-type agnostic - it forwards whatever the caller supplies - so the grid
      // is instantiated at its `GridRowBase` floor here, at this single boundary.
      <SlickGrid1
        {...rest}
        ref={gridRef}
        gridOptions={{ forceFitColumns: true }}
        dataset={dataset}
        remoteDataSource={remoteDataSource}
        query={isQueryShape(query) ? query : undefined}
        columnDefinitions={columnDefinitions}
        infiniteScroll
      />
    );
  },
);

TableComponent.displayName = "TableComponent";
export default TableComponent;
